import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import TutorialService from '../services/TutorialService';
import { UserTutorialProgress, buildTutorialHierarchy } from '../models/TutorialModels';

const TutorialContext = createContext();

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
};

export const TutorialProvider = ({ children }) => {
  const auth = useAuth();
  const [tutorialStructure, setTutorialStructure] = useState([]);
  const [userProgress, setUserProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dataReady, setDataReady] = useState(false);
  const [error, setError] = useState(null);
  const [selectedContent, setSelectedContent] = useState(null);
  const [contentUrl, setContentUrl] = useState(null);

  // Get current user ID
  const userId = auth.user?.profile?.email;

  /**
   * Load tutorial structure from API
   */
  const loadTutorialStructure = async () => {
    try {
      setError(null);
      
      const response = await TutorialService.getTutorialStructure();
      const hierarchicalData = buildTutorialHierarchy(response.structure);
      setTutorialStructure(hierarchicalData);
      
      return response;
    } catch (err) {
      console.error('Error loading tutorial structure:', err);
      setError(err.message);
      throw err;
    }
  };

  /**
   * Load user progress from API
   */
  const loadUserProgress = async () => {
    if (!userId) return;

    try {
      setError(null);
      
      const response = await TutorialService.getUserProgress(userId);
      const progress = new UserTutorialProgress(response);
      setUserProgress(progress);
      
      return progress;
    } catch (err) {
      console.error('Error loading user progress:', err);
      
      // If user not found, try to initialize using resolveRequiredStatusForUser
      // The updated resolveRequiredStatusForUser can now handle non-existent users by creating them
      if (err.message.includes('User not found')) {
        try {
          await initializeUserProgress();
        } catch (initErr) {
          console.error('Error initializing user progress:', initErr);
          // If initialization still fails, there might be a system issue
          setError('Failed to initialize user tutorial status.');
        }
      } else {
        setError(err.message);
      }
    }
  };

  /**
   * Initialize user tutorial progress
   */
  const initializeUserProgress = async (settingMode = 'default', groupId = null) => {
    if (!userId) return;

    try {
      setError(null);
      
      const response = await TutorialService.initializeUserTutorialStatus(userId, settingMode, groupId);
      const progress = new UserTutorialProgress({
        userId: userId,
        tutorial_content_status: response.tutorial_content_status,
        hasStatus: true
      });
      setUserProgress(progress);
      
      return progress;
    } catch (err) {
      console.error('Error initializing user progress:', err);
      setError(err.message);
      throw err;
    }
  };

  /**
   * Update content completion status
   */
  const updateContentProgress = async (itemId, completed) => {
    if (!userId) return;

    try {
      setError(null);
      
      const response = await TutorialService.updateUserProgress(userId, itemId, completed);
      const updatedProgress = new UserTutorialProgress({
        userId: userId,
        tutorial_content_status: response.tutorial_content_status,
        hasStatus: true
      });
      setUserProgress(updatedProgress);
      
      return updatedProgress;
    } catch (err) {
      console.error('Error updating content progress:', err);
      setError(err.message);
      throw err;
    }
  };

  /**
   * Get presigned URL for content
   */
  const getContentUrl = async (s3Key, contentType) => {
    try {
      setError(null);
      
      const response = await TutorialService.getPresignedURL(s3Key, contentType);
      return response.presignedUrl;
    } catch (err) {
      console.error('Error getting content URL:', err);
      setError(err.message);
      throw err;
    }
  };

  /**
   * Select content and load its URL
   */
  const selectContent = async (content) => {
    try {
      setSelectedContent(content);
      setContentUrl(null);
      
      if (content && content.s3Key) {
        const url = await getContentUrl(content.s3Key, content.contentType);
        setContentUrl(url);
      }
    } catch (err) {
      console.error('Error selecting content:', err);
      setError(err.message);
    }
  };

  /**
   * Clear selected content
   */
  const clearSelectedContent = () => {
    setSelectedContent(null);
    setContentUrl(null);
  };

  /**
   * Check if a module should be locked based on prerequisites
   */
  const isModuleLocked = (module, isAdminMode = false) => {
    if (!dataReady || !userProgress || !module) return false;

    // In admin mode, don't lock modules based on user progress
    if (isAdminMode) return false;

    // If a module (including submodule) contains only optional content, it should never be locked
    // This checks across all nested content inside the module
    const getAllContent = (mod) => {
      if (!mod) return [];
      if (typeof mod.getAllContent === 'function') {
        return mod.getAllContent();
      }
      const collectContent = (items) => {
        const content = [];
        if (items && Array.isArray(items)) {
          items.forEach(item => {
            if (item.type === 'content') {
              content.push(item);
            } else if (item.children && Array.isArray(item.children)) {
              content.push(...collectContent(item.children));
            }
          });
        }
        return content;
      };
      return collectContent(mod.children || []);
    };

    const allContentInModule = getAllContent(module);
    if (allContentInModule.length > 0) {
      const hasAnyRequired = allContentInModule.some(contentItem => {
        const status = userProgress.getContentStatus(contentItem.itemId);
        return !!status?.required;
      });
      if (!hasAnyRequired) {
        return false; // All content is optional -> never lock
      }
    }
    
    // If any direct child submodule has only optional content, the parent module should not be locked
    const directChildModules = (module.children || []).filter(child => child.type === 'module');
    const hasOptionalOnlySubmodule = directChildModules.some(childModule => {
      const childContent = getAllContent(childModule);
      if (childContent.length === 0) return false; // treat empty as not qualifying this rule
      const childHasRequired = childContent.some(contentItem => {
        const status = userProgress.getContentStatus(contentItem.itemId);
        return !!status?.required;
      });
      return !childHasRequired;
    });
    if (hasOptionalOnlySubmodule) {
      return false;
    }
    
    // Helper function to find module by itemId
    const findModuleById = (items, targetId) => {
      for (const item of items) {
        if (item.itemId === targetId) return item;
        if (item.children && item.children.length > 0) {
          const found = findModuleById(item.children, targetId);
          if (found) return found;
        }
      }
      return null;
    };
    
    // If this is a child module, check sibling modules first
    // Note: modules with parentId 'root' should be treated as top-level modules
    if (module.parentId && module.parentId !== 'root') {
      const parentModule = findModuleById(tutorialStructure, module.parentId);
      if (parentModule) {
        // Get all sibling modules (modules with the same parent) in order
        const siblingModules = parentModule.children
          .filter(child => child.type === 'module')
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        
        // Find current module's position among siblings
        const currentModuleIndex = siblingModules.findIndex(m => m.itemId === module.itemId);
        
        // If this is not the first sibling module, check if previous siblings are completed
        if (currentModuleIndex > 0) {
          for (let i = 0; i < currentModuleIndex; i++) {
            const previousSibling = siblingModules[i];
            if (!isModuleCompleted(previousSibling)) {
              return true; // Previous sibling not completed, lock this module
            }
          }
        }
        
        // Also check if parent module is locked
        if (isModuleLocked(parentModule)) {
          return true; // Parent module is locked, so this module is also locked
        }
      }
    } else {
      // For top-level modules, check if previous top-level modules are completed
      const topLevelModules = tutorialStructure.filter(item => 
        item.type === 'module' && (!item.parentId || item.parentId === '' || item.parentId === 'root')
      ).sort((a, b) => (a.order || 0) - (b.order || 0));
      
      // Find the current module's position
      const currentModuleIndex = topLevelModules.findIndex(m => m.itemId === module.itemId);
      
      // If this is not the first module, check if previous modules are completed
      if (currentModuleIndex > 0) {
        for (let i = 0; i < currentModuleIndex; i++) {
          const previousModule = topLevelModules[i];
          const isCompleted = isModuleCompleted(previousModule);
          
          if (!isCompleted) {
            return true; // Previous module not completed, lock this module
          }
        }
      }
    }
    
    return false; // All prerequisites met, don't lock
  };

  /**
   * Check if a module is completed (all required content finished)
   */
  const isModuleCompleted = (module) => {
    if (!dataReady || !userProgress || !module) return false;
    
    // Get all content from the module
    let allContent = [];
    
    if (typeof module.getAllContent === 'function') {
      allContent = module.getAllContent();
    } else {
      const collectContent = (items) => {
        const content = [];
        if (items && Array.isArray(items)) {
          items.forEach(item => {
            if (item.type === 'content') {
              content.push(item);
            } else if (item.children && Array.isArray(item.children)) {
              content.push(...collectContent(item.children));
            }
          });
        }
        return content;
      };
      allContent = collectContent(module.children || []);
    }
    
    // Get required and optional content separately
    const requiredContent = allContent.filter(content => {
      const status = userProgress.getContentStatus(content.itemId);
      return status.required;
    });
    
    const optionalContent = allContent.filter(content => {
      const status = userProgress.getContentStatus(content.itemId);
      return !status.required;
    });
    
    // If there are required contents, check if all required content is completed
    if (requiredContent.length > 0) {
      return requiredContent.every(content => 
        userProgress.isContentCompleted(content.itemId)
      );
    }
    
    // If all content is optional, only show completion if ALL optional content is completed
    if (optionalContent.length > 0) {
      return optionalContent.every(content => 
        userProgress.isContentCompleted(content.itemId)
      );
    }
    
    // If no content at all, consider module completed
    return true;
  };

  /**
   * Get module completion status
   */
  const getModuleCompletionStatus = (module) => {
    if (!userProgress || !module) return { completed: 0, total: 0, percentage: 0 };
    
    // Get all content from the module
    let allContent = [];
    
    if (typeof module.getAllContent === 'function') {
      // If it's a proper TutorialModule instance
      allContent = module.getAllContent();
    } else {
      // Fallback: manually collect content from children
      const collectContent = (items) => {
        const content = [];
        if (items && Array.isArray(items)) {
          items.forEach(item => {
            if (item.type === 'content') {
              content.push(item);
            } else if (item.children && Array.isArray(item.children)) {
              content.push(...collectContent(item.children));
            }
          });
        }
        return content;
      };
      
      allContent = collectContent(module.children || []);
    }
    
    const total = allContent.length;
    const completed = allContent.filter(content => 
      userProgress.isContentCompleted(content.itemId)
    ).length;
    
    return {
      completed,
      total,
      percentage: total > 0 ? Math.round((completed / total) * 100) : 0
    };
  };

  // Load initial data when component mounts or user changes
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        setDataReady(false);
        
        // Load tutorial structure first
        await loadTutorialStructure();
        
        // Then load user progress if user is available
        if (userId) {
          await loadUserProgress();
        }
        
        // Mark data as ready only after both are loaded
        setDataReady(true);
      } catch (err) {
        console.error('Error loading initial tutorial data:', err);
      } finally {
        // Only stop loading after both are complete
        setLoading(false);
      }
    };

    loadInitialData();
  }, [userId]);

  const value = {
    // State
    tutorialStructure,
    userProgress,
    loading,
    dataReady,
    error,
    selectedContent,
    contentUrl,
    userId,

    // Methods
    loadTutorialStructure,
    loadUserProgress,
    initializeUserProgress,
    updateContentProgress,
    getContentUrl,
    selectContent,
    clearSelectedContent,
    isModuleLocked,
    isModuleCompleted,
    getModuleCompletionStatus,

    // Utility methods
    setError: (err) => setError(err),
    clearError: () => setError(null),
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};

export default TutorialContext;
